import { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { useReactivateInvestment } from './useReactivateInvestment';

const LABEL = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-vega-muted';

/**
 * Modal de reativação de um investimento desinvestido. Comentário obrigatório. POST
 * /api/investments/{id}/reactivate (If-Match). O backend limpa as métricas realizadas (DEC-040).
 */
export function ReactivateModal({
  id,
  etag,
  codename,
  onClose,
}: {
  id: string;
  etag: string;
  codename: string;
  onClose: () => void;
}) {
  const reactivate = useReactivateInvestment();
  const [comment, setComment] = useState('');

  const canConfirm = comment.trim().length > 0 && !reactivate.isPending;

  const onConfirm = () => {
    if (!canConfirm) return;
    reactivate.mutate({ id, etag, comment }, { onSuccess: onClose });
  };

  return (
    <>
      <button
        type="button"
        aria-label="Cancelar"
        onClick={onClose}
        className="fixed inset-0 z-[90] cursor-default bg-[rgba(20,18,12,0.45)]"
      />
      <div
        role="dialog"
        aria-label="Reativar investimento"
        className="fixed left-1/2 top-1/2 z-[100] w-[min(420px,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-vega-surface p-5 shadow-vega-lg"
      >
        <div className="mb-1 flex items-center gap-2">
          <RotateCcw size={16} className="text-vega-ink-2" />
          <h2 className="text-[15px] font-semibold text-vega-ink">Reativar investimento</h2>
        </div>
        <p className="mb-4 text-[12.5px] text-vega-muted-2">
          Traz <span className="text-vega-ink">{codename}</span> de volta a Ativo. As métricas
          realizadas são limpas (o histórico preserva). Explique o motivo.
        </p>

        <label className="mb-4 block">
          <span className={LABEL}>Comentário *</span>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            autoFocus
            placeholder="Obrigatório — fica registrado no histórico do investimento."
            className="w-full resize-none rounded border border-vega-line bg-vega-surface px-2.5 py-2 text-[13px] text-vega-ink"
          />
        </label>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={reactivate.isPending}
            className="rounded border border-vega-line px-3 py-1.5 text-[12.5px] font-medium text-vega-muted disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className="rounded bg-vega-ink px-3 py-1.5 text-[12.5px] font-semibold text-vega-bg disabled:opacity-50"
          >
            {reactivate.isPending ? 'Salvando…' : 'Reativar'}
          </button>
        </div>
      </div>
    </>
  );
}
